<script setup lang="ts">
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { SHIPMENT_DETAIL_TRIM } from '@/components/shipment/detail/shipment-detail-trim';
import { SHIPMENT_DETAIL_COLUMN_BG } from '@/components/shipment/detail/shipment-detail-shell';

defineProps<{
  stopsCount: number;
  exceptionsCount: number;
}>();

const activeTab = defineModel<string>({ default: 'overview' });

const tabPanelPt = {
  root: { class: '!bg-transparent !p-0' },
};
</script>

<template>
  <Tabs
    v-model:value="activeTab"
    :pt="{
      root: {
        class: 'flex min-h-0 flex-1 flex-col bg-transparent',
      },
    }"
    class="flex min-h-0 flex-1 flex-col"
  >
    <TabList
      :pt="{
        root: {
          class:
            'flex shrink-0 gap-1 overflow-x-auto border-b border-neutral-200 bg-white px-3 md:px-4',
        },
      }"
    >
      <Tab
        value="overview"
        :pt="{
          root: ({ context }) => ({
            class: [
              'shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
              context.active
                ? 'border-primary text-primary'
                : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
            ],
          }),
        }"
      >
        Overview
      </Tab>
      <Tab
        value="stops"
        :pt="{
          root: ({ context }) => ({
            class: [
              'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
              context.active
                ? 'border-primary text-primary'
                : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
            ],
          }),
        }"
      >
        Stops
        <span
          v-if="stopsCount > 0"
          class="inline-flex min-w-5 items-center justify-center rounded-full bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold text-neutral-600"
        >
          {{ stopsCount }}
        </span>
      </Tab>
      <Tab
        value="exceptions"
        :pt="{
          root: ({ context }) => ({
            class: [
              'inline-flex shrink-0 items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
              context.active
                ? 'border-primary text-primary'
                : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
            ],
          }),
        }"
      >
        Exceptions
        <span
          v-if="exceptionsCount > 0"
          class="inline-flex min-w-5 items-center justify-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700"
        >
          {{ exceptionsCount }}
        </span>
      </Tab>
      <Tab
        v-if="!SHIPMENT_DETAIL_TRIM.activityTab"
        value="activity"
        :pt="{
          root: ({ context }) => ({
            class: [
              'shrink-0 border-b-2 px-3 py-2 text-sm font-medium transition-colors -mb-px',
              context.active
                ? 'border-primary text-primary'
                : 'border-transparent text-neutral-500 hover:border-neutral-300 hover:text-neutral-700',
            ],
          }),
        }"
      >
        Activity
      </Tab>
    </TabList>

    <TabPanels
      :pt="{
        root: {
          class:
            'min-h-0 flex-1 overflow-y-auto px-3 pb-4 pt-4 md:px-4',
          style: { backgroundColor: SHIPMENT_DETAIL_COLUMN_BG },
        },
      }"
    >
      <TabPanel value="overview" :pt="tabPanelPt">
        <slot name="overview" />
      </TabPanel>
      <TabPanel value="stops" :pt="tabPanelPt">
        <slot name="stops" />
      </TabPanel>
      <TabPanel value="exceptions" :pt="tabPanelPt">
        <slot name="exceptions" />
      </TabPanel>
      <TabPanel
        v-if="!SHIPMENT_DETAIL_TRIM.activityTab"
        value="activity"
        :pt="tabPanelPt"
      >
        <slot name="activity" />
      </TabPanel>
    </TabPanels>
  </Tabs>
</template>
